import { UserPaymentDetailsFormContentFieldsProps } from '../props';
import { useFormContext } from 'react-hook-form';
import { FormGroup, Input } from '@starsoft/common/components';
import styles from '../../styles.module.scss';
import {
  CreateUserPaymentDetailsInputDto,
  UpdateUserPaymentDetailsInputDto,
} from '@starsoft/common/dto';
import { UserPaymentDetailsFormFields } from '@starsoft/common/enums';
import {
  useCoallescedUserPaymentTypeId,
  useTranslation,
} from '../../../../../../../../../hooks';
import { ImpsInputDto } from '../../../../../../../../../dto/user-payment-details/create-user-payment-details-input/imps';
import { UserPaymentDetailsValidationResult } from '../../../../../../../../../models/user-payment-details/validation/result';
import { useValidateUserPaymentDetails } from '../../../../../../../../../api';
import { ImpsPayload } from '../../../../../../../../../interfaces/user-payment-details/validation-result/imps';

/**
 * UserPaymentDetailsFormContentUpi Component
 * This component is responsible for rendering the form fields specific to IMPS payment details.
 * It handles validation and displays appropriate error messages or success indicators.
 *
 * @param {UserPaymentDetailsFormContentFieldsProps} props - The properties for the component.
 * @param {boolean} props.isPending - Flag indicating whether the form is in a pending state.
 *
 * @returns {JSX.Element} The rendered component displaying the IMPS payment details form fields.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsFormContentUpi isPending={false} />
 */
export default function UserPaymentDetailsFormContentUpi({
  isPending,
}: UserPaymentDetailsFormContentFieldsProps) {
  const { t } = useTranslation('common');
  const { register, setValue, watch, clearErrors, setError } = useFormContext<
    CreateUserPaymentDetailsInputDto & UpdateUserPaymentDetailsInputDto
  >();
  const typeId: string = useCoallescedUserPaymentTypeId();
  const imps: ImpsInputDto & Partial<ImpsInputDto> = watch('imps');

  /**
   * onValidationResultSuccess Function
   * This function is triggered upon successful validation of user payment details.
   * It clears previous errors and sets new values in the form context based on the validation result.
   *
   * @param {UserPaymentDetailsValidationResult} validationResult - The result of the validation process.
   */
  function onValidationResultSuccess(
    validationResult: UserPaymentDetailsValidationResult,
  ) {
    clearErrors();

    if (validationResult?.errors?.length > 0) {
      setError('imps.accountHolder', {
        type: 'validate',
      });
    }

    if (validationResult?.imps?.accountHolder) {
      setValue('imps.accountHolder', validationResult?.imps?.accountHolder);
    }

    if (validationResult?.imps?.ifsc) {
      setValue('imps.ifsc', validationResult?.imps?.ifsc);
    }

    if (validationResult?.imps?.accountNumber) {
      setValue('imps.accountNumber', validationResult?.imps?.accountNumber);
    }
  }

  const {
    isPending: isPendingValidation,
    validateUserPaymentDetails,
    validationResult,
    errorMapping,
  } = useValidateUserPaymentDetails({
    onSuccess: onValidationResultSuccess,
  });

  const accountHolderErrors = errorMapping?.get(
    UserPaymentDetailsFormFields.ImpsAccountHolder,
  );
  const ifscErrors = errorMapping?.get(UserPaymentDetailsFormFields.ImpsIfsc);
  const bankAccountErrors = errorMapping?.get(
    UserPaymentDetailsFormFields.BankAccountNumber,
  );

  const isAccountHolderError: boolean =
    !isPendingValidation && accountHolderErrors?.length > 0;
  const isIfscError: boolean = !isPendingValidation && ifscErrors?.length > 0;
  const isBankAccountError: boolean =
    !isPendingValidation && bankAccountErrors?.length > 0;

  /**
   * onValidationInputBlur Function
   * This function is triggered when an input field loses focus.
   * It validates the user payment details using the current input value.
   *
   * @param {keyof ImpsPayload} key - The key of the input field being validated.
   * @param {string} value - The current value of the input field.
   *
   * @example
   * // Usage example:
   * onValidationInputBlur('accountHolder', 'John Doe');
   */
  function onValidationInputBlur(key: keyof ImpsPayload, value: string) {
    validateUserPaymentDetails({
      typeId,
      imps: {
        ...imps,
        [key]: value,
      },
    });
  }

  return (
    <>
      <FormGroup
        label={t('account_holder_label')}
        isLoading={isPending}
        loading={{ label: true, secondary: true }}
      >
        <Input
          className={
            !isAccountHolderError && !!validationResult
              ? styles.container__input
              : ''
          }
          name="imps.accountHolder"
          isLoading={isPending}
          isError={isAccountHolderError}
          errorMessage={accountHolderErrors}
          showSpinner={isPendingValidation}
          register={register}
          placeholder={t('account_holder_label')}
          rightIcon={
            !isAccountHolderError && !!validationResult
              ? 'fa-solid fa-circle-check'
              : undefined
          }
          onBlur={() =>
            onValidationInputBlur('accountHolder', imps?.accountHolder)
          }
        />
      </FormGroup>

      <FormGroup
        label={t('ifsc_label')}
        isLoading={isPending}
        loading={{ label: true, secondary: true }}
      >
        <Input
          className={
            !isIfscError && !!validationResult ? styles.container__input : ''
          }
          name="imps.ifsc"
          isLoading={isPending}
          isError={isIfscError}
          errorMessage={ifscErrors}
          showSpinner={isPendingValidation}
          register={register}
          placeholder={t('ifsc_label')}
          rightIcon={
            !isIfscError && !!validationResult
              ? 'fa-solid fa-circle-check'
              : undefined
          }
          onBlur={() => onValidationInputBlur('ifsc', imps?.ifsc)}
        />
      </FormGroup>

      <FormGroup
        label={t('bank_account_label')}
        isLoading={isPending}
        loading={{ label: true, secondary: true }}
      >
        <Input
          className={
            !isBankAccountError && !!validationResult
              ? styles.container__input
              : ''
          }
          name="imps.accountNumber"
          isLoading={isPending}
          isError={isBankAccountError}
          errorMessage={bankAccountErrors}
          showSpinner={isPendingValidation}
          register={register}
          placeholder={t('bank_account_label')}
          rightIcon={
            !isBankAccountError && !!validationResult
              ? 'fa-solid fa-circle-check'
              : undefined
          }
          onBlur={() =>
            onValidationInputBlur('accountNumber', imps?.accountNumber)
          }
        />
      </FormGroup>
    </>
  );
}
